import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckDouble, faLayerGroup, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import purchaseOrderService from '../../services/purchaseOrderService';
import { useCoilSettings } from '../../context/CoilSettingsContext';
import CoilEntrySheet from '../coils/CoilEntrySheet';
import { splitCoilAndProrataWeights, formatKg, formatMetres } from '../../utils/coilMath';
import { isPieceUnit } from '../../utils/units';

const sanitizeNumber = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const s = String(v);
    if (!/^\d*\.?\d*$/.test(s)) return s.slice(0, -1);
    return s;
};

/**
 * Coil rows as this screen holds them — the POST /coils body minus the fields only the
 * receipt knows (warehouse, product size). Tolerates a saved draft coming back either in
 * the shape we sent it or as full coil records, so a truck that was half typed when the
 * operator hit Save re-opens with every row intact.
 */
const normalizeCoilEntries = (entries) => (Array.isArray(entries) ? entries : []).map(entry => ({
    batch_number: entry?.batch_number ?? '',
    supplier_batch_ref: entry?.supplier_batch_ref ?? null,
    colour: entry?.colour ?? null,
    received_date: entry?.received_date || '',
    weight_kg: entry?.weight_kg ?? entry?.original_weight_kg ?? '',
    length_m: entry?.length_m ?? entry?.original_length_m ?? '',
}));

/** A coil line's weight: the sum of its coils, in whole kg like the ledger stores them. */
const coilWeightOf = (item) => (item?.coil_entries || []).reduce((sum, coil) => {
    const kg = parseFloat(coil?.weight_kg);
    return Number.isFinite(kg) ? sum + Math.round(kg) : sum;
}, 0);

/** A coil line's length: summed per coil, since each coil has its own kg-per-metre. */
const coilLengthOf = (item) => (item?.coil_entries || []).reduce((sum, coil) => {
    const metres = parseFloat(coil?.length_m);
    return Number.isFinite(metres) ? sum + metres : sum;
}, 0);

/** Truck weights read in thousands on the shop floor: 8,240 rather than 8240. */
const groupKg = (value) => Math.round(parseFloat(value) || 0).toLocaleString('en-IN');

const CompleteUnloadingForm = ({ isOpen, onClose, onSubmit, adviceId, productSizes = [], mode = 'complete' }) => {
    const { t } = useTranslation();
    const isEditMode = mode === 'edit';

    const [loading, setLoading] = useState(false);
    const [advice, setAdvice] = useState(null);

    const [formData, setFormData] = useState({
        empty_truck_weight: '',
        loaded_truck_weight: '',
        remark: '',
        internal_remark: '',
        items_update: [],
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [saving, setSaving] = useState(false);
    // Set when the advice could not be loaded: the form must not be submitted on
    // top of stale/empty seed data (policy C19).
    const [fetchError, setFetchError] = useState(null);
    const lastFetchedIdRef = useRef(null);

    // Which line's coil sheet is open (index into items_update), or null for none.
    const [coilSheetIndex, setCoilSheetIndex] = useState(null);

    const { coilTrackingEnabled } = useCoilSettings();

    // Two-level gate: the global setting AND the size's own flag. With either one off the
    // set is empty, every line behaves exactly as it did before, and nothing coil-shaped
    // renders at all.
    // The advice line now says so itself. Deriving it from `productSizes` was unreliable:
    // the page loads only the first 20 sizes, so any coil-tracked SKU past that was
    // treated as ordinary — the coil sheet never appeared, and receipt then failed with
    // "no coils were recorded" and no way for the operator to act on it. The list is kept
    // as a fallback for a response that predates the field.
    const coilTrackedSizeIds = useMemo(() => {
        if (!coilTrackingEnabled) return new Set();
        return new Set(
            (productSizes || []).filter(p => p.coil_tracked).map(p => String(p.id))
        );
    }, [coilTrackingEnabled, productSizes]);

    const isCoilItem = useCallback(
        (item) => {
            if (!coilTrackingEnabled) return false;
            if (item?.coil_tracked != null) return !!item.coil_tracked;
            return coilTrackedSizeIds.has(String(item?.product_size_id));
        },
        [coilTrackingEnabled, coilTrackedSizeIds]
    );

    useEffect(() => {
        if (isOpen && adviceId && lastFetchedIdRef.current !== adviceId) {
            lastFetchedIdRef.current = adviceId;
            const fetchData = async () => {
                setLoading(true);
                try {
                    const adviceResult = await purchaseOrderService.getPurchaseAdviceById(adviceId);

                    if (adviceResult.success && adviceResult.data) {
                        const adviceData = adviceResult.data;
                        setAdvice(adviceData);
                        setFormData({
                            empty_truck_weight: isEditMode && adviceData.empty_truck_weight ? String(adviceData.empty_truck_weight) : '',
                            loaded_truck_weight: adviceData.loaded_truck_weight || adviceData.final_truck_weight || adviceData.empty_truck_weight || '',
                            remark: adviceData.remark || '',
                            internal_remark: adviceData.internal_remark || '',
                            items_update: (adviceData.items || []).map(item => ({
                                id: item.id,
                                product_size_id: item.product_size_id,
                                product_name: item.size_name || item.product_size?.alias_name || item.product_size?.product_name || 'Unknown Item',
                                ordered_quantity: parseFloat(item.quantity) || 0,
                                quantity: isEditMode && item.actual_quantity != null ? String(item.actual_quantity) : item.quantity,
                                unit: item.unit,
                                price: item.price,
                                length: item.length,
                                po_item_quantity: item.po_item_quantity,
                                actual_pieces: isEditMode && item.actual_pieces != null ? String(item.actual_pieces) : '',
                                remark: item.remark,
                                // Carried through so `isCoilItem` works on the form row,
                                // not just on the raw advice item.
                                coil_tracked: !!item.coil_tracked,
                                // Coils already captured against this line — from a saved
                                // draft, or from a receipt being edited.
                                coil_entries: normalizeCoilEntries(item.coil_entries),
                            })),
                        });
                        setErrors({});
                        setCoilSheetIndex(null);
                    } else {
                        // The service returns {success:false} rather than throwing —
                        // block the form (including Save draft) here too.
                        lastFetchedIdRef.current = null;
                        setFetchError(adviceResult.error || t('common.messages.detailsLoadFailed'));
                    }
                } catch (error) {
                    console.error('Failed to fetch data', error);
                    lastFetchedIdRef.current = null;
                    setFetchError(t('common.messages.detailsLoadFailed'));
                } finally {
                    setLoading(false);
                }
            };
            setFetchError(null);
            fetchData();
        }

        if (!isOpen) {
            lastFetchedIdRef.current = null;
        }
    }, [isOpen, adviceId, isEditMode]);

    // Every coil weight on the receipt, as one primitive. Entering coils changes what the
    // ordinary lines have left to share, so the pro-rata pass below has to run again — but
    // only when a coil weight actually moved, not on every keystroke elsewhere.
    const coilWeightSignature = useMemo(
        () => formData.items_update.map(item => (isCoilItem(item) ? coilWeightOf(item) : '')).join('|'),
        [formData.items_update, isCoilItem]
    );

    // Pro-rata weight distribution logic
    useEffect(() => {
        if (!advice) return;

        const loadedWeight = parseFloat(formData.loaded_truck_weight) || 0;
        const emptyWeight = parseFloat(formData.empty_truck_weight) || 0;
        const netWeight = Math.round(loadedWeight - emptyWeight);

        if (emptyWeight > 0 && netWeight > 0) {
            setFormData(prev => {
                const itemsCount = prev.items_update.length;
                if (itemsCount === 0) return prev;

                // Coil lines were weighed one coil at a time before they went on the truck,
                // so their weight is measured fact, not a share of the total. They leave the
                // pool entirely and only what the truck carried beyond them is pro-rated —
                // which makes the estimate on the remaining lines strictly better.
                const { prorataNet } = splitCoilAndProrataWeights({
                    items: prev.items_update,
                    netWeight,
                    isCoilLine: isCoilItem,
                    weightOf: coilWeightOf,
                });

                // The remainder lands on the last line that is actually being pro-rated; a
                // coil line must never absorb it, its quantity is the sum of its coils.
                let lastProrataIndex = -1;
                prev.items_update.forEach((item, index) => {
                    if (!isCoilItem(item)) lastProrataIndex = index;
                });
                if (lastProrataIndex === -1) return prev; // every line is a coil line

                // 1. Calculate estimated weight basis for each item to handle mixed units (m, kg, pcs)
                const itemBases = prev.items_update.reduce((acc, item) => {
                    if (isCoilItem(item)) return acc;
                    const adviceItem = advice.items.find(ai => ai.id === item.id);
                    let basis = 0;
                    if (adviceItem) {
                        const sw = parseFloat(adviceItem.standard_weight) || 0;
                        const sl = parseFloat(adviceItem.standard_length) || 1;
                        const u = (item.unit || '').toLowerCase();
                        if (u === 'm') basis = item.ordered_quantity * (sw / sl);
                        else if (u === 'kg') basis = item.ordered_quantity;
                        else if (isPieceUnit(u)) basis = item.ordered_quantity * sw;
                        else basis = item.ordered_quantity;
                    } else {
                        basis = item.ordered_quantity;
                    }
                    acc.push({ id: item.id, basis });
                    return acc;
                }, []);

                const totalBasis = itemBases.reduce((acc, it) => acc + it.basis, 0);
                if (totalBasis <= 0) return prev;

                let distributedWeightTotal = 0;
                const newItemsUpdate = prev.items_update.map((item, index) => {
                    // Coil lines keep their measured quantity untouched.
                    if (isCoilItem(item)) return item;

                    const adviceItem = advice.items.find(ai => ai.id === item.id);
                    const itemBasis = itemBases.find(ib => ib.id === item.id)?.basis || 0;

                    // 2. Calculate Pro-rata Weight (Integer)
                    let itemWeight = 0;
                    if (index === lastProrataIndex) {
                        itemWeight = prorataNet - distributedWeightTotal;
                    } else {
                        itemWeight = Math.round((itemBasis / totalBasis) * prorataNet);
                        distributedWeightTotal += itemWeight;
                    }

                    // 3. Convert Weight back to Item's Input Unit (e.g. Meters)
                    let finalQuantityStr = String(itemWeight);
                    if (adviceItem) {
                        const sw = parseFloat(adviceItem.standard_weight) || 0;
                        const sl = parseFloat(adviceItem.standard_length) || 1;
                        const u = (item.unit || '').toLowerCase();
                        
                        if (u === 'm' && sw > 0) {
                            // meters = weight / (weight_per_meter)
                            const weightPerMeter = sw / sl;
                            finalQuantityStr = (itemWeight / weightPerMeter).toFixed(2);
                        }
                    }

                    return {
                        ...item,
                        quantity: finalQuantityStr,
                        actual_weight: itemWeight // Keep track of the raw distributed weight
                    };
                });

                // Check for actual changes to avoid unnecessary re-renders
                const hasChanged = newItemsUpdate.some((item, idx) => 
                    item.quantity !== prev.items_update[idx].quantity
                );

                return hasChanged ? { ...prev, items_update: newItemsUpdate } : prev;
            });
        } else if (!formData.empty_truck_weight || parseFloat(formData.empty_truck_weight) <= 0) {
            // FALLBACK: Reset to initial values from advice
            setFormData(prev => {
                const resetItems = prev.items_update.map(item => {
                    const adviceItem = advice.items.find(ai => ai.id === item.id);
                    if (!adviceItem) return item;
                    // A coil line has nothing to reset to: its quantity is the coils.
                    if (isCoilItem(item)) return item;

                    // Reconstruct initial values based on current mode
                    const initialQty = isEditMode && adviceItem.actual_quantity != null ? String(adviceItem.actual_quantity) : adviceItem.quantity;
                    const initialPieces = isEditMode && adviceItem.actual_pieces != null ? String(adviceItem.actual_pieces) : '';
                    
                    return {
                        ...item,
                        quantity: initialQty,
                        actual_pieces: initialPieces,
                        actual_weight: undefined // Clear the pro-rata weight cache
                    };
                });

                const hasChanged = resetItems.some((it, idx) => 
                    it.quantity !== prev.items_update[idx].quantity || 
                    it.actual_pieces !== prev.items_update[idx].actual_pieces
                );

                return hasChanged ? { ...prev, items_update: resetItems } : prev;
            });
        }
    }, [formData.empty_truck_weight, formData.loaded_truck_weight, advice, isEditMode, isCoilItem, coilWeightSignature]);

    const calculatedData = useMemo(() => {
        if (!advice || !advice.items || formData.items_update.length === 0) return { items: [], isValid: false };

        const grossW = parseFloat(formData.loaded_truck_weight) || 0;
        const emptyW = parseFloat(formData.empty_truck_weight) || 0;
        const netW = emptyW > 0 ? Math.round(grossW - emptyW) : 0;

        let allQuantitiesEntered = true;
        let totalItemsWeight = 0;
        let totalCoilWeight = 0;

        const finalItems = formData.items_update.map((item) => {
            // A coil line is not estimated at all: the quantity IS the sum of the coils,
            // and the piece count IS how many of them came off the truck.
            if (isCoilItem(item)) {
                const coilEntries = item.coil_entries || [];
                const coilKg = coilWeightOf(item);
                if (coilEntries.length === 0) allQuantitiesEntered = false;
                totalCoilWeight += coilKg;
                totalItemsWeight += coilKg;
                return {
                    ...item,
                    isCoil: true,
                    coilCount: coilEntries.length,
                    coilKg,
                    coilMetres: coilLengthOf(item),
                    derivedWeight: coilKg,
                };
            }

            if (item.quantity === '' || item.quantity === undefined) {
                allQuantitiesEntered = false;
            }

            // Use stored actual_weight if available, otherwise calculate from quantity
            let itemWeight = parseFloat(item.actual_weight);
            if (isNaN(itemWeight) || !item.actual_weight || emptyW === 0) {
                const adviceItem = advice.items.find(ai => ai.id === item.id);
                if (adviceItem) {
                    const sw = parseFloat(adviceItem.standard_weight) || 0;
                    const sl = parseFloat(adviceItem.standard_length) || 1;
                    const u = (item.unit || '').toLowerCase();
                    const qty = parseFloat(item.quantity) || 0;
                    if (u === 'm') itemWeight = qty * (sw / sl);
                    else itemWeight = qty;
                } else {
                    itemWeight = parseFloat(item.quantity) || 0;
                }
            }
            
            totalItemsWeight += itemWeight;
            return { ...item, isCoil: false, derivedWeight: itemWeight };
        });

        // The weighbridge also weighs dunnage, packing and whatever else rode along, so the
        // coils are NOT expected to add up to the net weight — a difference is a warning
        // chip, not an error. Coils weighing more than the truck could have carried is a
        // different animal: that is a mistyped tare, and it blocks.
        const coilExceedsNet = netW > 0 && totalCoilWeight > netW * 1.02;

        return {
            items: finalItems,
            netW,
            emptyW,
            totalItemsWeight,
            totalCoilWeight,
            coilExceedsNet,
            isValid: allQuantitiesEntered && emptyW > 0 && grossW > emptyW && !coilExceedsNet
        };
    }, [advice, formData.items_update, formData.empty_truck_weight, formData.loaded_truck_weight, isCoilItem]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'empty_truck_weight' || name === 'loaded_truck_weight') {
            const sanitized = sanitizeNumber(value);
            setFormData(prev => ({ ...prev, [name]: sanitized }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items_update];
        if (field === 'quantity') {
            newItems[index][field] = sanitizeNumber(value);
            // Clear actual_weight so calculatedData recomputes it from the new quantity
            delete newItems[index].actual_weight;
        } else {
            newItems[index][field] = value;
        }
        setFormData(prev => ({ ...prev, items_update: newItems }));
    };

    // The sheet hands back rows in the POST /coils shape; they live on the line until the
    // receipt (or the draft) is saved.
    const handleCoilsSaved = (index, coils) => {
        setFormData(prev => ({
            ...prev,
            items_update: prev.items_update.map((item, i) => (
                i === index ? { ...item, coil_entries: normalizeCoilEntries(coils) } : item
            )),
        }));
        setErrors(prev => ({ ...prev, [`item_${index}_coils`]: null, coil_total: null }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.empty_truck_weight) newErrors.empty_truck_weight = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidWeight');
        else if (isNaN(formData.empty_truck_weight) || parseFloat(formData.empty_truck_weight) <= 0) {
            newErrors.empty_truck_weight = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidWeight');
        }

        if (!formData.loaded_truck_weight) newErrors.loaded_truck_weight = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidWeight');
        else if (isNaN(formData.loaded_truck_weight) || parseFloat(formData.loaded_truck_weight) <= 0) {
            newErrors.loaded_truck_weight = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidWeight');
        }

        if (!formData.remark || !formData.remark.trim()) newErrors.remark = t('common.formFields.errors.orderRemarkRequired');

        formData.items_update.forEach((item, index) => {
            if (isCoilItem(item)) {
                // Nothing to reconcile: the coils ARE the quantity. The only thing that can
                // be wrong is that none were entered, in which case the line received nothing.
                if ((item.coil_entries || []).length === 0) {
                    newErrors[`item_${index}_coils`] = t('coils.errors.coilsRequired');
                }
                return;
            }
            if (item.quantity === '' || parseFloat(item.quantity) < 0) {
                newErrors[`item_${index}_quantity`] = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidQuantity');
            }
            // A pcs line is billed per piece (policy A7): the count is mandatory and is
            // never pre-filled from the order, so an uncounted line cannot bill zero.
            if ((isPieceUnit(item.unit) || item.load_by_pieces)
                && (item.actual_pieces === '' || item.actual_pieces == null || parseInt(item.actual_pieces, 10) <= 0)) {
                newErrors[`item_${index}_pieces`] = t('pieces.loading.pcsCountRequired');
            }
        });

        // Name both numbers — the operator has to know which of the two is the wrong one,
        // and it is almost always the tare.
        if (calculatedData.coilExceedsNet) {
            newErrors.coil_total = t('coils.errors.coilsExceedNet', {
                coils: groupKg(calculatedData.totalCoilWeight),
                net: groupKg(calculatedData.netW),
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildPayload = () => ({
        empty_truck_weight: formData.empty_truck_weight !== '' ? parseFloat(formData.empty_truck_weight) : null,
        loaded_truck_weight: formData.loaded_truck_weight !== '' ? parseFloat(formData.loaded_truck_weight) : null,
        remark: formData.remark || null,
        internal_remark: formData.internal_remark || '',
        items_update: formData.items_update.map(item => {
            if (!isCoilItem(item)) {
                return {
                    item_id: item.id,
                    actual_quantity: parseFloat(item.quantity),
                    actual_pieces: item.actual_pieces !== '' ? parseInt(item.actual_pieces) : null,
                };
            }

            // Quantity and piece count are read off the coils themselves — the same numbers
            // the read-only cells show. Sent on the draft too, so a half-typed truck comes
            // back whole.
            const coilEntries = item.coil_entries || [];
            return {
                item_id: item.id,
                actual_quantity: coilWeightOf(item),
                actual_pieces: coilEntries.length,
                coil_entries: coilEntries.map(entry => ({
                    batch_number: String(entry.batch_number || '').trim(),
                    supplier_batch_ref: entry.supplier_batch_ref || null,
                    colour: entry.colour || null,
                    received_date: entry.received_date || null,
                    weight_kg: Math.round(parseFloat(entry.weight_kg) || 0),
                    length_m: parseFloat(entry.length_m) || 0,
                })),
            };
        }),
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            // Was fire-and-forget: the service returns {success,error} without throwing,
            // so a failed draft save (400/404/500) gave NO feedback and the operator
            // believed the half-entered truck/coil draft was saved — then lost it on
            // reload. Surface the outcome.
            const res = await purchaseOrderService.saveUnloadingDraft(adviceId, buildPayload());
            if (res && res.success === false) {
                alert(res.error || t('purchaseOrders.forms.unloading.draftSaveFailed', 'Could not save draft. Please try again.'));
            } else if (res && res.success === true) {
                alert(t('purchaseOrders.forms.unloading.draftSaved', 'Draft saved.'));
            }
        } catch (e) {
            alert(e?.message || t('purchaseOrders.forms.unloading.draftSaveFailed', 'Could not save draft. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            await onSubmit(buildPayload());
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const coilSheetItem = coilSheetIndex !== null ? formData.items_update[coilSheetIndex] : null;

    return (
        <>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: colorHelpers.withOpacity('#000000', 0.5) }}>
            <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col relative overflow-hidden" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                {(submitting || loading || saving) && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.6) }}>
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: UI_COLORS.BRAND.PRIMARY }}></div>
                    </div>
                )}

                <div className="flex items-center justify-between p-6 border-b flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{isEditMode ? t('common.formFields.labels.editUnloadingTitle') : t('purchaseOrders.forms.completeUnloadingForm.title')}</h2>
                    <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-gray-100" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar">
                    <p className="text-sm text-gray-500 mb-4">{t('purchaseOrders.forms.completeUnloadingForm.subtitle')}</p>

                    <div className="mb-6 space-y-4">
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.orderRemark')} <span>*</span></label>
                            <input
                                type="text"
                                name="remark"
                                value={formData.remark}
                                onChange={handleChange}
                                placeholder={t('common.formFields.placeholders.orderRemark')}
                                className="w-full px-4 py-2 rounded-xl border-2 transition-all outline-none text-sm"
                                style={{ borderColor: errors.remark ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                            />
                            {errors.remark && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.remark}</p>}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.internalRemarks')}</label>
                            <textarea
                                name="internal_remark"
                                value={formData.internal_remark}
                                onChange={handleChange}
                                placeholder={t('common.formFields.placeholders.internalRemarksOptional')}
                                className="w-full px-4 py-2 rounded-xl border-2 transition-all outline-none resize-none text-sm"
                                rows="2"
                                style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}
                            />
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {advice && (
                            <div className="p-4 rounded-xl mb-4 space-y-2" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.05), border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1)}` }}>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">{t('purchaseOrders.forms.completeUnloadingForm.fields.poNumber')}</span>
                                    <span className="font-bold" style={{ color: UI_COLORS.TEXT.BLACK }}>{advice.po_number || t('common.symbols.emDash')}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">{t('purchaseOrders.forms.completeUnloadingForm.fields.adviceNumber')}</span>
                                    <span className="font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{advice.advice_number || t('common.symbols.emDash')}</span>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrders.forms.completeUnloadingForm.fields.loadedWeight.label')} <span>*</span></label>
                                <input
                                    type="number"
                                    name="loaded_truck_weight"
                                    value={formData.loaded_truck_weight}
                                    onChange={handleChange}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder={t('common.formFields.placeholders.loadedWeight')}
                                    className="w-full px-4 py-3 rounded-xl border-2 transition-all outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    style={{ borderColor: errors.loaded_truck_weight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                />
                                {errors.loaded_truck_weight && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.loaded_truck_weight}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrders.forms.completeUnloadingForm.fields.emptyWeight.label')} <span>*</span></label>
                                <input
                                    type="number"
                                    name="empty_truck_weight"
                                    value={formData.empty_truck_weight}
                                    onChange={handleChange}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder={t('purchaseOrders.forms.completeUnloadingForm.placeholders.truckWeight')}
                                    className="w-full px-4 py-3 rounded-xl border-2 transition-all outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    style={{ borderColor: errors.empty_truck_weight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                />
                                {errors.empty_truck_weight && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.empty_truck_weight}</p>}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-3" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseOrders.forms.completeUnloadingForm.headings.items')}</h3>
                            <div className="space-y-3">
                                {calculatedData.items.map((item, index) => (
                                    <div key={item.id} className="p-4 rounded-xl border transition-colors hover:border-gray-300" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.MAIN }}>
                                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{item.product_name}</p>
                                                    {item.load_by_pieces && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200">{t('common.formFields.labels.loadByPieces')}</span>
                                                    )}
                                                    {item.isCoil && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200">{t('coils.receipt.pill')}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 mt-1">
                                                    <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                                        {t('purchaseOrders.forms.completeUnloadingForm.fields.ordered')}: {item.ordered_quantity} {item.unit}
                                                    </p>
                                                    {(() => {
                                                        const pcs = advice?.items?.find(ai => ai.id === item.id)?.quantity_in_pieces ?? item.quantity_in_pieces;
                                                        return pcs != null ? (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-sm font-bold text-blue-600">{pcs}</span>
                                                                <span className="text-[10px] font-bold uppercase text-gray-400 tracking-tighter">{t('common.units.pcs')}</span>
                                                            </div>
                                                        ) : null;
                                                    })()}
                                                </div>
                                                {item.remark && item.remark !== 'none' && (
                                                    <div className="mt-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-800 border border-yellow-200 uppercase tracking-tight">
                                                        {item.remark}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="w-full md:w-36">
                                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrders.forms.completeUnloadingForm.fields.unloadedQty')}</label>
                                                <div className="relative flex items-center">
                                                    {item.isCoil ? (
                                                        // Read-only: the quantity is the sum of the coils weighed below,
                                                        // so there is nothing here for anyone to type or disagree with.
                                                        <input
                                                            type="text"
                                                            value={formatKg(item.coilKg)}
                                                            readOnly
                                                            title={t('coils.receipt.derivedFromCoils')}
                                                            className="w-full px-3 py-2 rounded-lg border-2 outline-none pr-10 font-semibold cursor-default"
                                                            style={{
                                                                borderColor: UI_COLORS.BORDERS.LIGHT,
                                                                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.08),
                                                                color: UI_COLORS.TEXT.PRIMARY,
                                                            }}
                                                        />
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                            onWheel={(e) => e.target.blur()}
                                                            className="w-full px-3 py-2 rounded-lg border-2 transition-all outline-none focus:border-blue-500 pr-10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                            style={{ borderColor: errors[`item_${index}_quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                                        />
                                                    )}
                                                    <span className="absolute right-3 text-xs font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>{item.isCoil ? t('common.units.kg') : item.unit}</span>
                                                </div>
                                                {errors[`item_${index}_quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`item_${index}_quantity`]}</p>}
                                            </div>
                                            <div className="w-full md:w-32">
                                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.actualPieces')}</label>
                                                {item.isCoil ? (
                                                    // One piece is one coil — counted, not typed.
                                                    <input
                                                        type="text"
                                                        value={item.coilCount}
                                                        readOnly
                                                        title={t('coils.receipt.derivedFromCoils')}
                                                        className="w-full px-3 py-2 rounded-lg border-2 outline-none font-semibold cursor-default"
                                                        style={{
                                                            borderColor: UI_COLORS.BORDERS.LIGHT,
                                                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.08),
                                                            color: UI_COLORS.TEXT.PRIMARY,
                                                        }}
                                                    />
                                                ) : (
                                                    <>
                                                        <input
                                                            type="number"
                                                            value={item.actual_pieces}
                                                            onChange={(e) => handleItemChange(index, 'actual_pieces', e.target.value)}
                                                            onWheel={(e) => e.target.blur()}
                                                            placeholder="0"
                                                            className="w-full px-3 py-2 rounded-lg border-2 transition-all outline-none focus:border-blue-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                            style={{ borderColor: errors[`item_${index}_pieces`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                                        />
                                                        {errors[`item_${index}_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`item_${index}_pieces`]}</p>}
                                                    </>
                                                )}
                                            </div>
                                            <div className="md:w-32 flex flex-col justify-center bg-white p-2 rounded border text-sm shadow-sm" title={item.isCoil ? t('coils.receipt.derivedFromCoils') : t('common.messages.estimatedWeightTooltip')}>
                                                <div className="flex flex-col">
                                                    <span className="text-gray-400 text-[10px] uppercase font-bold tracking-tighter">{t('common.formFields.labels.calcWt')}</span>
                                                    <span className="font-bold text-gray-900">{parseFloat(item.derivedWeight || 0).toFixed(0)} {t('common.units.kg')}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Received-by-pieces banner: a pcs line is priced per piece, so the
                                            received PIECE count is the figure that matters. */}
                                        {!item.isCoil && (isPieceUnit(item.unit) || item.load_by_pieces) && (
                                            <div
                                                className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg text-xs font-medium"
                                                style={{
                                                    color: UI_COLORS.STATUS.INFO,
                                                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
                                                    border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.25)}`,
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faCheckDouble} className="mt-0.5" />
                                                <span>
                                                    {isPieceUnit(item.unit)
                                                        ? t('pieces.loading.pcsReceiveNote')
                                                        : t('pieces.loading.loadByPiecesNote')}
                                                </span>
                                            </div>
                                        )}

                                        {item.isCoil && (
                                            <div className="mt-3 pt-3 border-t flex flex-wrap items-center justify-between gap-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <FontAwesomeIcon icon={faLayerGroup} className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }} />
                                                    <span className="text-xs font-medium truncate" style={{ color: item.coilCount ? UI_COLORS.TEXT.PRIMARY : UI_COLORS.TEXT.MUTED }}>
                                                        {item.coilCount
                                                            ? t('coils.line.summaryWithLength', {
                                                                count: String(item.coilCount),
                                                                weight: groupKg(item.coilKg),
                                                                length: formatMetres(item.coilMetres),
                                                            })
                                                            : t('coils.receipt.noneEntered')}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    {errors[`item_${index}_coils`] && (
                                                        <span className="text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`item_${index}_coils`]}</span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setCoilSheetIndex(index)}
                                                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105"
                                                        style={{
                                                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.12),
                                                            color: UI_COLORS.BRAND.TERTIARY,
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={item.coilCount ? faPen : faPlus} />
                                                        {item.coilCount ? t('coils.line.editCoils') : t('coils.receipt.enterCoils')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between items-center gap-4 pt-4 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1) }}>
                                    <span className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">{t('common.formFields.labels.netTruckWeight')}</span>
                                    <span className="text-lg font-black" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{calculatedData.netW || '0'} {t('common.units.kg')}</span>
                                </div>
                                {calculatedData.coilExceedsNet && (
                                    <div className="max-w-md px-3 py-2 rounded-lg text-xs font-semibold" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1), color: UI_COLORS.STATUS.ERROR }}>
                                        {t('coils.errors.coilsExceedNet', {
                                            coils: groupKg(calculatedData.totalCoilWeight),
                                            net: groupKg(calculatedData.netW),
                                        })}
                                    </div>
                                )}
                                {(isEditMode || calculatedData.emptyW > 0) && Math.abs(calculatedData.netW - Math.round(calculatedData.totalItemsWeight)) > 0 && (
                                    <div className="flex items-center gap-1 ml-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse"></span>
                                        <span className="text-[10px] text-red-600 font-black uppercase tracking-tighter">
                                            {calculatedData.totalItemsWeight > calculatedData.netW ? 'Weight Overage' : 'Weight Shortage'}: {Math.abs(calculatedData.netW - Math.round(calculatedData.totalItemsWeight))} {t('common.units.kg')}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {fetchError && <p className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{fetchError}</p>}
                            <div className="flex gap-4">
                                <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl font-medium transition-colors" style={{ backgroundColor: '#F3F4F6', color: '#374151' }}>{t('common.buttons.cancel')}</button>
                                <button type="button" onClick={handleSave} disabled={saving || submitting || !!fetchError} className="px-6 py-3 rounded-xl font-medium transition-colors disabled:opacity-50" style={{ backgroundColor: '#E5E7EB', color: '#374151' }}>{saving ? t('common.buttons.saving') : t('common.buttons.save')}</button>
                                <button type="submit" disabled={!calculatedData.isValid || submitting || !!fetchError} className="px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:scale-100" style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY, color: UI_COLORS.TEXT.PRIMARY }}>
                                    <FontAwesomeIcon icon={faCheckDouble} className="mr-2" />
                                    {t('purchaseOrders.forms.completeUnloadingForm.buttons.complete')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        {/* Mounted only while open, and outside the <form> so the sheet's own Enter-to-next-row
            handling can never reach the receipt's submit. */}
        {coilSheetItem && (
            <CoilEntrySheet
                isOpen
                onClose={() => setCoilSheetIndex(null)}
                initialCoils={coilSheetItem.coil_entries || []}
                productName={coilSheetItem.product_name}
                unitLabel={coilSheetItem.unit}
                expectedQuantity={coilSheetItem.ordered_quantity}
                onSave={(coils) => handleCoilsSaved(coilSheetIndex, coils)}
            />
        )}
        </>
    );
};

export default CompleteUnloadingForm;
